import React, { useRef, useEffect, useState } from "react";
import HLS from "hls.js/dist/hls.light.min";
import { cx } from "utils/join-classnames";
import { Icon, Button, Spinner, VolumeControls, ScrubBar } from "ui/components";
import { VideoState, VideoActionRequest } from "core/models";
import createImageFromVideo from "utils/imageFromVideo";
import styles from "./VideoPlayer.module.scss";

const VIDEO_FPS = 25;

interface Props {
    type: "stream" | "video";
    videoSrc: string;
    autoplay?: boolean | "end";
    onTimeUpdate?(time: number): void;
    onPlaying?(status: boolean): void;
    overlay?(videoState: VideoState): React.ReactNode;
    controls?: boolean;
    trim?: [number, number];
    actionRequest?: VideoActionRequest;
    useClickOverlay?: boolean;
    poster?: string;
    onScreenshot?(screenshot: string): void;
    onEnded?(): void;
    subtitleTrackSrc?: string;
    onUpdateDuration?(duration: number): void;
    verticalResize?: boolean;
    externalVideoRef?: React.MutableRefObject<HTMLVideoElement>;
}

const timeDisplay = (
    currentTime: number,
    duration: number,
    type: Props["type"],
) => {
    let timePlayed = new Date(currentTime * 1000).toISOString().substr(11, 8);
    timePlayed =
        timePlayed.substr(0, 2) === "00" ? timePlayed.substr(3) : timePlayed;
    let totalDuration = "00:00";
    if (duration) {
        totalDuration = new Date(duration * 1000).toISOString().substr(11, 8);
    }
    totalDuration =
        totalDuration.substr(0, 2) === "00"
            ? totalDuration.substr(3)
            : totalDuration;
    if (type === "stream") {
        return `LIVE: ${timePlayed || "00:00"}`;
    }
    return `${timePlayed || "00:00"}/ ${totalDuration || "00:00"}`;
};

const VideoPlayer: React.FC<Props> = ({
    type,
    videoSrc,
    autoplay: requestedAutoplay = false,
    onTimeUpdate,
    overlay,
    controls = true,
    trim = [0, 0],
    actionRequest,
    onPlaying,
    useClickOverlay = true,
    poster,
    onScreenshot,
    onEnded,
    onUpdateDuration,
    subtitleTrackSrc,
    verticalResize = false,
    externalVideoRef,
}) => {
    const animationFrame = useRef<number | null>(null);
    const localVideoRef =
        useRef<HTMLVideoElement>() as React.MutableRefObject<HTMLVideoElement>;
    const videoRef = externalVideoRef || localVideoRef;
    const [playing, setPlaying] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [stalled, setStalled] = useState<boolean>(false);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [duration, setDuration] = useState<number>(0);
    const [volume, setVolume] = useState<number>(1);
    const [volumeMemory, setVolumeMemory] = useState<number>(1);
    const [muted, setMuted] = useState<boolean>(false);
    const [ended, setEnded] = useState<boolean>(false);
    const [size, setSize] = useState<"small" | "medium" | "large">("medium");
    const playPromise = useRef<Promise<void>>();
    const [volumeSlider, setVolumeSlider] = useState<boolean>(false);
    const [, setError] = useState<Error | null>(null);
    const [videoAutoplay, setVideoAutoplay] = useState<boolean | "end">(
        requestedAutoplay,
    );
    const playingRef = useRef<boolean>(false);

    const [trimStart, trimEnd] = trim;

    const getThumbfromFrame = async () => {
        if (!videoRef.current) {
            return;
        }
        const blob = await createImageFromVideo(videoRef.current);
        if (!blob) {
            return;
        }
        const thumbnailURL = URL.createObjectURL(blob);
        if (onScreenshot) {
            onScreenshot(thumbnailURL);
        }
    };

    const togglePlay = (): void => {
        const video = videoRef.current;
        if (video) {
            if (!playing) {
                playPromise.current = video
                    .play()
                    .catch((err) => setError(err));
                setPlaying(true);
            } else {
                video.pause();
                setPlaying(false);
                if (onScreenshot) {
                    getThumbfromFrame();
                }
            }
        }
    };

    const handleSetTime = (newTime: number) => {
        videoRef.current.currentTime = newTime;
    };

    const handlePlay = () => {
        playPromise.current = videoRef.current
            .play()
            .catch((err) => setError(err));
        setPlaying(true);
    };

    const handlePause = () => {
        if (playPromise.current !== undefined) {
            playPromise.current.then(() => {
                videoRef.current.pause();
            });
        }
        setPlaying(false);
    };

    const handleVolumeChange = (newValue: number) => {
        videoRef.current.volume = newValue;
        setVolume(newValue);
        setMuted(false);
    };

    const handleMuteChange = (newStatus: boolean) => {
        if (newStatus) {
            setVolumeMemory(volume);
        }
        setMuted(newStatus);
        setVolume(newStatus ? 0 : volumeMemory);
    };

    const handleFullscreen = () => {
        if (!document.fullscreenElement) {
            videoRef.current.requestFullscreen();
        } else if (document.exitFullscreen) {
            document.exitFullscreen();
        }
    };

    const handleVolumeSliderToggle = (state: boolean) => {
        setVolumeSlider(state);
    };

    const addSubtitleTrack = (url: string): void => {
        videoRef.current.querySelectorAll("track").forEach((el) => el.remove());
        const track = document.createElement("track");
        track.kind = "captions";
        track.label = "English";
        track.default = true;
        track.srclang = "en";
        track.src = url;
        videoRef.current.append(track);
        videoRef.current.textTracks[0].mode = "showing";
    };

    const forceStopVideo = (): void => {
        handlePause();
        setVideoAutoplay(false);
        setEnded(true);
    };

    const animate = () => {
        if (
            !videoRef.current ||
            !playingRef.current ||
            trimStart === undefined ||
            trimEnd === undefined
        ) {
            return;
        }
        const calculatedDuration = duration - (trimStart + trimEnd);
        const calculatedTime = videoRef.current.currentTime - trimStart;

        if (videoRef.current.currentTime < trimStart) {
            videoRef.current.currentTime = trimStart;
        } else if (calculatedTime === calculatedDuration) {
            forceStopVideo();
        } else if (calculatedTime > calculatedDuration) {
            forceStopVideo();
            videoRef.current.currentTime = duration - trimEnd;
        }
        animationFrame.current = requestAnimationFrame(animate);
    };

    useEffect(() => {
        if (!subtitleTrackSrc) {
            return;
        }
        addSubtitleTrack(subtitleTrackSrc);
    }, [subtitleTrackSrc]);

    const handleReplay = () => {
        videoRef.current.currentTime = 0;
        playPromise.current = videoRef.current
            .play()
            .catch((err) => setError(err));
        setEnded(false);
    };

    useEffect(() => {
        if (!actionRequest) {
            return;
        }
        if (!videoRef || actionRequest.type === "none") {
            return;
        }
        switch (actionRequest.type) {
            case "load":
                videoRef.current.load();
                break;
            case "togglePlay":
                togglePlay();
                break;
            case "play":
                handlePlay();
                break;
            case "pause":
                handlePause();
                if (onScreenshot) {
                    getThumbfromFrame();
                }
                break;
            case "rewind":
                videoRef.current.currentTime = 0 + trimStart;
                playPromise.current = videoRef.current
                    .play()
                    .catch((err) => setError(err));
                break;
            case "setTime":
                videoRef.current.currentTime = actionRequest.value || 0;
                break;
            case "setTimePaused":
                handlePause();
                videoRef.current.currentTime = actionRequest.value || 0;
                break;
            case "frameForwards":
                setVideoAutoplay(false);
                videoRef.current.currentTime += 1 / VIDEO_FPS;
                break;
            case "frameBackwards":
                setVideoAutoplay(false);
                videoRef.current.currentTime -= 1 / VIDEO_FPS;
                break;
            case "playFromStart":
                videoRef.current.currentTime = 0;
                playPromise.current = videoRef.current
                    .play()
                    .catch((err) => setError(err));
                break;
            case "playLastSeconds":
                videoRef.current.currentTime =
                    (duration || 0) - trimEnd - (actionRequest.value || 0);
                playPromise.current = videoRef.current
                    .play()
                    .catch((err) => setError(err));
                break;
            case "getScreenshot":
                handlePause();
                getThumbfromFrame();
                break;
            default:
                throw Error("Default case actionRequest");
        }
    }, [actionRequest]);

    useEffect(() => {
        if (!videoRef.current) {
            return;
        }
        const video = videoRef.current;
        const updateSize = () => {
            if (video) {
                if (video.offsetWidth < 400) {
                    setSize("small");
                } else if (video.offsetWidth > 800) {
                    setSize("large");
                } else {
                    setSize("medium");
                }
            }
        };
        updateSize();
        window.addEventListener("resize", updateSize);

        return () => {
            window.removeEventListener("resize", updateSize);
        };
    }, [videoRef]);

    useEffect(() => {
        if (!onPlaying) {
            return;
        }
        onPlaying(playing);
    }, [playing, onPlaying]);

    useEffect(() => {
        if (requestedAutoplay === videoAutoplay) {
            return;
        }
        setVideoAutoplay(requestedAutoplay);
    }, [requestedAutoplay]);

    const resetAnimationframe = (): void => {
        if (animationFrame.current) {
            cancelAnimationFrame(animationFrame.current);
        }
    };
    useEffect(() => {
        if (type === "stream") {
            return;
        }
        if (playing) {
            animationFrame.current = requestAnimationFrame(animate);
        }
        playingRef.current = playing;
        return () => resetAnimationframe();
    }, [playing]);

    const handleVideoEvent = (e: any) => {
        if (!videoRef.current) {
            return;
        }
        // TODO fix any
        switch (e.type) {
            case "playing":
                setPlaying(true);
                playingRef.current = true;
                setEnded(false);
                setStalled(false);
                break;
            case "paused":
                setPlaying(false);
                playingRef.current = false;
                break;
            case "canplay": // Fires when the browser can start playing the audio/video
                setLoading(false);
                setStalled(false);
                if (videoAutoplay === "end" && !playing) {
                    videoRef.current.currentTime = duration - 1 || 0;
                    handlePlay();
                }
                break;
            case "canplaythrough": // Fires when the browser can play through the audio/video without stopping for buffering
                setLoading(false);
                setStalled(false);
                if (requestedAutoplay) {
                    handlePlay();
                }
                break;
            case "progress": // Fires when the browser is downloading the audio/video
                break;
            case "loadstart": // Fires when the browser starts looking for the audio/video
                setVideoAutoplay(requestedAutoplay);
                setLoading(true);
                break;
            case "seeking": // Fires when the user starts moving/skipping to a new position in the audio/video
                setEnded(false);
                break;
            case "seeked": // Fires when the user is finished moving/skipping to a new position in the audio/video
                setEnded(false);
                break;
            case "stalled": // Fires when the browser is trying to get media data, but data is not available
                setStalled(true);
                break;
            case "waiting": // Fires when the browser is trying to get media data, but data is not available
                break;
            case "timeupdate": // Fires when the current playback position has changed
                setCurrentTime(e.target.currentTime);
                if (onTimeUpdate) {
                    onTimeUpdate(e.target.currentTime);
                }
                break;
            case "ended": // Returns whether the playback of the audio/video has ended or not
                setEnded(true);
                setPlaying(false);
                if (onPlaying) {
                    onPlaying(false);
                }
                if (onEnded) {
                    onEnded();
                }
                break;
            case "loadeddata": // Fires when the browser has loaded the current frame of the audio/video
                break;
            case "loadedmetadata": // Fires when the metadata is loaded
                setDuration(e.target.duration);
                if (onUpdateDuration) {
                    onUpdateDuration(e.target.duration);
                }
                break;
            default:
                throw Error("Default case VideoEvent");
        }
    };

    useEffect(() => {
        const video = videoRef.current;
        if (!video) {
            return undefined;
        }
        if (onScreenshot) {
            getThumbfromFrame();
        }
        let destroyHLS: (() => void) | null = null;
        if (type === "stream" && HLS.isSupported()) {
            const player = new HLS();
            player.attachMedia(video);
            player.loadSource(videoSrc);
            destroyHLS = player.destroy.bind(player);
        }
        if (type === "video") {
            video.src = videoSrc;
        }
        setPlaying(false);
        return () => destroyHLS?.();
    }, [videoSrc]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) {
            return undefined;
        }

        video.addEventListener("playing", handleVideoEvent, { passive: true });
        video.addEventListener("paused", handleVideoEvent, { passive: true });
        video.addEventListener("canplay", handleVideoEvent, { passive: true });
        video.addEventListener("canplaythrough", handleVideoEvent, {
            passive: true,
        });
        video.addEventListener("progress", handleVideoEvent, { passive: true });
        video.addEventListener("loadstart", handleVideoEvent, {
            passive: true,
        });
        video.addEventListener("seeking", handleVideoEvent, { passive: true });
        video.addEventListener("seeked", handleVideoEvent, { passive: true });
        video.addEventListener("stalled", handleVideoEvent, { passive: true });
        video.addEventListener("waiting", handleVideoEvent, { passive: true });
        video.addEventListener("timeupdate", handleVideoEvent, {
            passive: true,
        });
        video.addEventListener("ended", handleVideoEvent, { passive: true });
        video.addEventListener("loadeddata", handleVideoEvent, {
            passive: true,
        });
        video.addEventListener("loadedmetadata", handleVideoEvent, {
            passive: true,
        });
        return () => {
            video.removeEventListener("playing", handleVideoEvent);
            video.removeEventListener("paused", handleVideoEvent);
            video.removeEventListener("canplay", handleVideoEvent);
            video.removeEventListener("canplaythrough", handleVideoEvent);
            video.removeEventListener("progress", handleVideoEvent);
            video.removeEventListener("loadstart", handleVideoEvent);
            video.removeEventListener("seeking", handleVideoEvent);
            video.removeEventListener("seeked", handleVideoEvent);
            video.removeEventListener("stalled", handleVideoEvent);
            video.removeEventListener("waiting", handleVideoEvent);
            video.removeEventListener("timeupdate", handleVideoEvent);
            video.removeEventListener("ended", handleVideoEvent);
            video.removeEventListener("loadeddata", handleVideoEvent);
            video.removeEventListener("loadedmetadata", handleVideoEvent);
        };
    }, [videoSrc, trim]);

    return (
        <div
            className={cx(
                styles.video__container,
                size === "large" ? styles.large : null,
                size === "small" ? styles.small : null,
                verticalResize ? styles["video__container--vertical"] : null,
            )}
        >
            {overlay && (
                <div className={styles.video__overlay__custom}>
                    {overlay({
                        playing,
                        buffering: loading,
                        duration: duration - (trimStart + trimEnd),
                        currentTime: (currentTime || 0) - trimStart,
                        setCurrentTime: handleSetTime,
                        play: handlePlay,
                        pause: handlePause,
                        vidRef: videoRef,
                    })}
                </div>
            )}
            {controls && (
                <div className={styles.video__overlay__default}>
                    <div
                        className={styles.clickoverlay}
                        role="presentation"
                        onClick={useClickOverlay ? togglePlay : undefined}
                    >
                        {!playing && !loading && !stalled && (
                            <div
                                className={styles.btn__play__main}
                                role="presentation"
                                onClick={togglePlay}
                            >
                                <Icon name="play" />
                            </div>
                        )}
                        {ended && !stalled && (
                            <div
                                className={styles.btn__play__main}
                                role="presentation"
                                onClick={handleReplay}
                            >
                                <Icon name="reload" />
                            </div>
                        )}
                    </div>

                    {type !== "stream" && (
                        <div className={styles.progressbar}>
                            <ScrubBar vidRef={videoRef} size={size} />
                        </div>
                    )}

                    <div className={styles.buttonbar}>
                        <Button
                            type="function"
                            icon={playing ? "pause" : "play"}
                            onClick={togglePlay}
                            size={size !== "small" ? "large" : "medium"}
                        />
                        <div className={styles.volume__controls}>
                            <VolumeControls
                                volumeValue={volume}
                                muted={muted}
                                onChange={handleVolumeChange}
                                onMuteChange={handleMuteChange}
                                size={size}
                                onSliderToggle={handleVolumeSliderToggle}
                            />
                        </div>
                        {volumeSlider && size === "small" ? null : (
                            <div className={styles.timedisplay}>
                                {timeDisplay(currentTime, duration, type)}
                            </div>
                        )}
                        <div className={styles.spacer} />
                        {document.fullscreenEnabled && (
                            <Button
                                type="function"
                                icon="fullscreen"
                                size={size !== "small" ? "large" : "medium"}
                                onClick={handleFullscreen}
                            />
                        )}
                    </div>
                </div>
            )}
            {loading && !overlay && (
                <div className={styles.video__loading}>
                    <span className={styles.video__loading__text}>
                        <Spinner size={size === "small" ? "small" : "medium"} />
                    </span>
                </div>
            )}

            <video
                crossOrigin="anonymous"
                className={styles.video__player}
                ref={videoRef}
                muted={muted}
                autoPlay={videoAutoplay === true}
                controls={false}
                playsInline
                poster={poster}
            />
        </div>
    );
};

export default VideoPlayer;
